import { readNativeFile } from "./native-file";
import { randomId } from "./random-id";
import { Capacitor } from "@capacitor/core";
import { App, type RestoredListenerEvent } from "@capacitor/app";
import { persistSharedDraft, sharedDraftKey } from "../features/feed/sharedDraft";

const pendingKey = "ll:native-capture-pending";
export type CapturePurpose = "post" | "profile";
type PendingCapture = { purpose?: CapturePurpose; id: string; ownerKey: string; plugin: "Camera" | "SeasonVideo"; result?: RestoredListenerEvent };
let ownerKey: string | undefined;
export function setCaptureRecoveryScope(user?: string | null, season?: string | null) {
  ownerKey = user && season ? sharedDraftKey(user, season) : undefined;
  return ownerKey;
}
function pending(): PendingCapture | undefined {
  const raw = localStorage.getItem(pendingKey);
  if (!raw) return;
  try { return JSON.parse(raw) as PendingCapture; } catch { localStorage.removeItem(pendingKey); return; }
}
export function beginRecoverableCapture(plugin: PendingCapture["plugin"], purpose: CapturePurpose = "post") {
  if (!ownerKey) throw new Error("Wait for your account to load before opening the camera.");
  // Do not overwrite bytes that still need recovering from an earlier process.
  if (pending()?.result) throw new Error("Your previous capture is still being recovered. Please reopen the app before taking another.");
  const capture: PendingCapture = { id: randomId(), ownerKey, plugin, purpose };
  localStorage.setItem(pendingKey, JSON.stringify(capture));
  return capture.id;
}
export function finishRecoverableCapture(id: string) {
  if (pending()?.id === id) localStorage.removeItem(pendingKey);
}
export function captureMatches(capture: PendingCapture, event: RestoredListenerEvent) {
  return capture.plugin === event.pluginId &&
    (event.pluginId === "Camera" && event.methodName === "getPhoto" || event.pluginId === "SeasonVideo" && event.methodName === "capture");
}
export async function recoveredCaptureFile(event: RestoredListenerEvent): Promise<File> {
  const data = event.data as Record<string, unknown>;
  const path = typeof data.path === "string" ? data.path : undefined;
  if (!path) throw new Error("The recovered capture could not be read.");
  const blob = await readNativeFile(path);
  if (!blob.size || blob.size > 25 * 1024 * 1024) throw new Error("The recovered capture is empty or exceeds the 25 MB media limit.");
  const video = event.pluginId === "SeasonVideo";
  const format = typeof data.format === "string" && /^(jpeg|jpg|png|webp|heic)$/.test(data.format) ? data.format : "jpeg";
  return new File([blob], video ? "recovered-video.mp4" : `recovered-photo.${format}`, {
    type: video ? "video/mp4" : `image/${format === "jpg" ? "jpeg" : format}`,
  });
}
export async function startMediaRecovery(onRecovered: (owner: string) => void, onError: (error: unknown, purpose?: CapturePurpose) => void) {
  if (!Capacitor.isNativePlatform()) return () => {};
  let recovering: Promise<void> | undefined;
  async function recover() {
    if (recovering) return recovering;
    const capture = pending();
    if (!capture?.result) return;
    // Profile photos need the crop/confirmation step. Never turn one into a post
    // or navigate away from the interrupted profile/onboarding screen.
    if (capture.purpose === "profile") {
      finishRecoverableCapture(capture.id);
      onError(new Error("Please select your profile photo again to crop and save it."), "profile");
      return;
    }
    recovering = (async () => {
      try {
        const file = await recoveredCaptureFile(capture.result!);
        // Commit before clearing the receipt. A second delivery is idempotent.
        await persistSharedDraft(capture.ownerKey, { batchId: `capture:${capture.id}`, text: "", files: [file] });
        finishRecoverableCapture(capture.id);
        onRecovered(capture.ownerKey);
      } catch (error) { onError(error); }
      finally { recovering = undefined; }
    })();
    return recovering;
  }
  const handle = await App.addListener("appRestoredResult", event => {
    const capture = pending();
    if (!capture || !captureMatches(capture, event)) return;
    if (!event.success) { finishRecoverableCapture(capture.id); return; }
    localStorage.setItem(pendingKey, JSON.stringify({ ...capture, result: event }));
    void recover();
  });
  await recover();
  return () => { void handle.remove(); };
}
